import { useEffect, useState } from "react";

import {
  useDataViewController,
  type DataViewController,
  type DataViewRouteState,
} from "~/views/viewController";
import { DEFAULT_PROJECT_TASK_VIEW_STATE, type DataViewState } from "~/views/viewDefinition";
import type { ProjectTaskCreateTarget } from "./taskViewTypes";
import { useProjectMilestones, type ProjectMilestonesController } from "./useProjectMilestones";

type ProjectTaskComposerState =
  | { readonly kind: "closed" }
  | { readonly kind: "open"; readonly target: ProjectTaskCreateTarget | null };

type MilestoneManagerState =
  | { readonly kind: "closed" }
  | { readonly kind: "open"; readonly milestoneId: number | null };

export interface ProjectTaskChrome {
  readonly viewController: DataViewController;
  readonly composerOpen: boolean;
  readonly composerTarget: ProjectTaskCreateTarget | null;
  readonly setComposerOpen: (open: boolean) => void;
  readonly toggleComposer: () => void;
  readonly openComposerFor: (target: ProjectTaskCreateTarget) => void;
  readonly milestoneManagerOpen: boolean;
  readonly milestoneManagerTargetId: number | null;
  readonly setMilestoneManagerOpen: (open: boolean) => void;
  readonly openMilestoneManager: (milestoneId: number) => void;
  readonly milestones: ProjectMilestonesController;
}

export interface UseProjectTaskChromeParams {
  readonly projectId: number | null;
  readonly routeViewState: DataViewRouteState;
  readonly onViewStateChange: (
    state: DataViewState,
    savedViewId: string | null,
    previousState: DataViewState,
    previousSavedViewId: string | null,
  ) => void;
  readonly savedViewsEnabled?: boolean;
  /** When false, skips saved-view queries (unused fallback chrome). */
  readonly enabled?: boolean;
}

/**
 * Shared task-tab chrome state: data-view controller + new-task composer.
 * Owned by project home when the toolbar lives in the tabs trailing slot.
 */
export function useProjectTaskChrome(params: UseProjectTaskChromeParams): ProjectTaskChrome {
  const enabled = (params.enabled ?? true) && params.projectId != null && params.projectId > 0;
  const viewController = useDataViewController({
    surface: "cmmd.project.tasks",
    ...(params.projectId != null && params.projectId > 0 ? { projectId: params.projectId } : {}),
    enabled,
    route: params.routeViewState,
    fallbackState: DEFAULT_PROJECT_TASK_VIEW_STATE,
    onRouteChange: params.onViewStateChange,
  });
  const [composer, setComposer] = useState<ProjectTaskComposerState>({ kind: "closed" });
  const [milestoneManager, setMilestoneManager] = useState<MilestoneManagerState>({
    kind: "closed",
  });
  const milestones = useProjectMilestones(params.projectId, enabled);

  useEffect(() => {
    setComposer({ kind: "closed" });
    setMilestoneManager({ kind: "closed" });
  }, [params.projectId]);

  return {
    viewController,
    composerOpen: composer.kind === "open",
    composerTarget: composer.kind === "open" ? composer.target : null,
    setComposerOpen: (open) =>
      setComposer(open ? { kind: "open", target: null } : { kind: "closed" }),
    toggleComposer: () =>
      setComposer((current) =>
        current.kind === "open" ? { kind: "closed" } : { kind: "open", target: null },
      ),
    openComposerFor: (target) => setComposer({ kind: "open", target }),
    milestoneManagerOpen: milestoneManager.kind === "open",
    milestoneManagerTargetId:
      milestoneManager.kind === "open" ? milestoneManager.milestoneId : null,
    setMilestoneManagerOpen: (open) =>
      setMilestoneManager(open ? { kind: "open", milestoneId: null } : { kind: "closed" }),
    openMilestoneManager: (milestoneId) => setMilestoneManager({ kind: "open", milestoneId }),
    milestones,
  };
}
